import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { NumberStatsComponent } from '../../../projects/my-app/src/app/number-stats/number-stats.component';
import { GlobalDropdownComponent } from '../../global-components/global-dropdown/global-dropdown.component';
import { MultiselectToggle } from '../../global-components/multiselect-toggle/multiselect-toggle';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';

@Component({
  selector: 'app-pat-haul-truck-screen',
  standalone: true,
  imports: [CommonModule, FormsModule, GlobalDropdownComponent, MultiselectToggle, NumberStatsComponent],
  templateUrl: './pat-haul-truck-screen.component.html',
  styleUrl: './pat-haul-truck-screen.component.scss',
})
export class PatHaulTruckScreenComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly GAP_DEBUG_KEY = 'PAT_WHITE_SPACE_GAP_DEBUG_V1';
  private static readonly HEADER_FILTER_DEBUG_KEY = 'PAT_HEADER_FILTERS_DEBUG_V1';

  @Input() data: any;
  @ViewChild('noteCardRef') noteCardRef?: ElementRef<HTMLElement>;
  @ViewChild('topStatHostRef', { read: ElementRef }) topStatHostRef?: ElementRef<HTMLElement>;

  headerFiltersState: any[] = [];
  filterSelections: Record<string, any> = {};
  variableInputs: Array<{ label: string; value: number; step?: number }> = [];
  maintenanceRows: Array<{ label: string; value: string | number; override?: string }> = [];
  optionalParts: Array<{ label: string; selected?: boolean }> = [];
  overhaulParts: Array<{ label: string; selected?: boolean }> = [];
  costRows: Array<any> = [];
  editableRows: Array<{ label: string; value: number; override: number }> = [];
  toggleSelections: Record<string, string[]> = {};
  private debugRafId: number | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
  ) {}

  ngOnInit(): void {
    this.initializeHeaderFiltersState();
    this.resetContentState();
    this.loadHeaderFilters();
  }

  ngAfterViewInit(): void {
    this.registerGapDebugHook();
    this.scheduleGapDebugLog('after-view-init');
  }

  ngOnDestroy(): void {
    if (this.debugRafId !== null) {
      cancelAnimationFrame(this.debugRafId);
      this.debugRafId = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  get headerFilters(): any[] {
    return this.headerFiltersState;
  }

  get topStat(): any {
    return this.data?.topStatCard || {};
  }

  get bottomStats(): any[] {
    return this.data?.bottomStats || [];
  }

  get noteText(): string {
    return this.data?.topNote || this.topStat?.note || '';
  }

  get optionalPartsTotal(): number {
    return Number(this.data?.optionalPartsTotal || 0);
  }

  resetFilters(): void {
    this.resetContentState();
    this.applyHeaderFilterSelections(true);
  }

  onToggleSelectionChange(selection: Record<string, string[]>): void {
    this.toggleSelections = selection;
  }

  trackByLabel(index: number, item: { label?: string; name?: string; title?: string }): string {
    return item.label || item.name || item.title || String(index);
  }

  private initializeHeaderFiltersState(): void {
    this.headerFiltersState = (this.data?.headerFilters || []).map((item: any) => ({ ...item }));
    this.filterSelections = {};
  }

  private resetContentState(): void {
    this.variableInputs = (this.data?.variableInputs || []).map((item: any) => ({ ...item }));
    this.maintenanceRows = (this.data?.maintenanceRows || []).map((item: any) => ({ ...item }));
    this.optionalParts = (this.data?.optionalParts || []).map((item: any) => ({ ...item }));
    this.overhaulParts = (this.data?.overhaulParts || []).map((item: any) => ({ ...item }));
    this.costRows = (this.data?.costTable?.rows || []).map((item: any) => ({ ...item }));
    this.editableRows = (this.data?.editableTable?.rows || []).map((item: any) => ({ ...item }));
    this.toggleSelections = { ...(this.data?.toggleConfig?.selected || {}) };
  }

  private loadHeaderFilters(): void {
    const url = String(this.data?.headerFilterApiUrl || '').trim();
    if (!url) {
      this.applyHeaderFilterSelections(true);
      return;
    }

    const payload = this.data?.headerFilterPayload || {};
    this.configService
      .getDropdownOptions(
        url,
        payload,
        this.data?.headerFilterMethod || 'post',
        this.data?.headerFilterHeaders,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const normalized = this.unwrapHeaderFilterResponse(response);
          this.initializeHeaderFiltersState();
          this.headerFiltersState = this.headerFiltersState.map((filter: any) => {
            const responseKey = String(filter.sourceKey || filter.key || '').trim();
            const responseOptions = normalized?.[responseKey];
            const options = this.normalizeDropdownOptions(responseOptions);
            return {
              ...filter,
              options: options.length > 0 ? options : filter.options || [],
            };
          });

          this.applyHeaderFilterSelections(true);
          this.logHeaderFilterDebug(response, normalized);
        },
        error: (error: any) => {
          console.error(
            `[${PatHaulTruckScreenComponent.HEADER_FILTER_DEBUG_KEY}] header filter load failed`,
            error,
          );
          this.initializeHeaderFiltersState();
          this.applyHeaderFilterSelections(true);
        },
      });
  }

  private applyHeaderFilterSelections(emit = false): void {
    this.headerFiltersState.forEach((filter: any, index: number) => {
      const options = Array.isArray(filter.options) ? filter.options : [];
      const defaultValue = filter.defaultValue;
      const resolvedValue = this.resolveDefaultFilterValue(defaultValue, options, filter.isMultiSelect);

      this.filterSelections[filter.key] = resolvedValue;
      this.headerFiltersState[index] = {
        ...filter,
        defaultValue: resolvedValue,
      };

      if (resolvedValue !== null && resolvedValue !== undefined && resolvedValue !== '') {
        this.globalFilterService.updateFilterSelection(
          filter.key,
          resolvedValue,
          filter.isQueryParam === true,
          this.data?.widgetId,
          false,
          filter.filterListener || null,
        );
      } else {
        this.globalFilterService.deleteKey(filter.key, filter.isQueryParam === true);
      }
    });

    if (emit) {
      this.globalFilterService.emitCurrentFilters();
    }
  }

  private unwrapHeaderFilterResponse(response: any): Record<string, any> {
    return (
      response?.responseList?.[0]?.data?.[0] ||
      response?.responseList?.[0]?.data ||
      response?.data?.[0] ||
      response?.data ||
      response ||
      {}
    );
  }

  private normalizeDropdownOptions(raw: any): any[] {
    if (!Array.isArray(raw)) {
      return [];
    }

    const normalized = raw
      .map((item: any) => {
        if (item === null || item === undefined) {
          return null;
        }

        if (typeof item === 'object') {
          return item.value ?? item.label ?? item.name ?? null;
        }

        return item;
      })
      .filter((item: any) => item !== null && item !== undefined && item !== '');

    return Array.from(new Set(normalized));
  }

  private resolveDefaultFilterValue(defaultValue: any, options: any[], isMultiSelect: boolean): any {
    if (isMultiSelect) {
      const requested = Array.isArray(defaultValue)
        ? defaultValue
        : defaultValue !== undefined && defaultValue !== null && defaultValue !== ''
          ? [defaultValue]
          : [];
      const matched = requested.filter((value: any) => options.includes(value));
      if (matched.length > 0) {
        return matched;
      }
      return options.length > 0 ? [options[0]] : [];
    }

    if (defaultValue !== undefined && defaultValue !== null && options.includes(defaultValue)) {
      return defaultValue;
    }

    return options.length > 0 ? options[0] : null;
  }

  private logHeaderFilterDebug(response: any, normalized: Record<string, any>): void {
    console.group(`[${PatHaulTruckScreenComponent.HEADER_FILTER_DEBUG_KEY}] loaded`);
    console.log({
      apiUrl: this.data?.headerFilterApiUrl || null,
      payload: this.data?.headerFilterPayload || {},
      normalizedKeys: Object.keys(normalized || {}),
      filters: this.headerFiltersState.map((filter: any) => ({
        name: filter.name,
        key: filter.key,
        sourceKey: filter.sourceKey || filter.key,
        optionCount: Array.isArray(filter.options) ? filter.options.length : 0,
        selected: this.filterSelections[filter.key],
      })),
      rawResponse: response,
    });
    console.groupEnd();
  }

  private registerGapDebugHook(): void {
    (window as any).__PAT_WHITE_SPACE_GAP_DEBUG__ = (reason = 'manual-trigger') => {
      this.logGapDebug(reason);
    };
  }

  private scheduleGapDebugLog(reason: string): void {
    this.debugRafId = requestAnimationFrame(() => {
      this.debugRafId = requestAnimationFrame(() => {
        this.logGapDebug(reason);
        this.debugRafId = null;
      });
    });
  }

  private logGapDebug(reason: string): void {
    const noteCard = this.noteCardRef?.nativeElement;
    const statHost = this.topStatHostRef?.nativeElement;
    const statRoot = statHost?.querySelector('.number-stats') as HTMLElement | null;
    const statCard = statHost?.querySelector('.stat-card') as HTMLElement | null;
    const statContent = statHost?.querySelector('.card-content') as HTMLElement | null;
    const noteLine = noteCard?.querySelector('.note-line') as HTMLElement | null;

    if (!noteCard || !statHost) {
      console.warn(`[${PatHaulTruckScreenComponent.GAP_DEBUG_KEY}] missing debug refs`, {
        hasNoteCard: !!noteCard,
        hasStatHost: !!statHost,
        reason,
      });
      return;
    }

    const payload = {
      reason,
      widgetId: this.data?.widgetId || null,
      noteCard: this.measureElement(noteCard),
      noteLine: this.measureElement(noteLine),
      topStatHost: this.measureElement(statHost),
      numberStatsRoot: this.measureElement(statRoot),
      statCard: this.measureElement(statCard),
      cardContent: this.measureElement(statContent),
      gapEstimatePx: this.computeGapEstimate(noteCard, noteLine, statCard),
    };

    console.group(`[${PatHaulTruckScreenComponent.GAP_DEBUG_KEY}] top stat whitespace`);
    console.log(payload);
    console.groupEnd();
  }

  private measureElement(element: HTMLElement | null): any {
    if (!element) {
      return null;
    }

    const rect = element.getBoundingClientRect();
    const styles = getComputedStyle(element);

    return {
      className: element.className || null,
      clientHeight: element.clientHeight,
      offsetHeight: element.offsetHeight,
      rectHeight: Number(rect.height.toFixed(2)),
      scrollHeight: element.scrollHeight,
      display: styles.display,
      height: styles.height,
      minHeight: styles.minHeight,
      maxHeight: styles.maxHeight,
      paddingTop: styles.paddingTop,
      paddingBottom: styles.paddingBottom,
      marginTop: styles.marginTop,
      marginBottom: styles.marginBottom,
      alignItems: styles.alignItems,
      justifyContent: styles.justifyContent,
      flexDirection: styles.flexDirection,
      gap: styles.gap,
    };
  }

  private computeGapEstimate(
    noteCard: HTMLElement,
    noteLine: HTMLElement | null,
    statCard: HTMLElement | null,
  ): number {
    const noteLineHeight = noteLine?.getBoundingClientRect().height || 0;
    const statCardHeight = statCard?.getBoundingClientRect().height || 0;
    const noteCardRect = noteCard.getBoundingClientRect();
    return Number(Math.max(0, noteCardRect.height - noteLineHeight - statCardHeight).toFixed(2));
  }
}
